/**
 * BlockedUsers — members the signed-in user has blocked, with an unblock
 * confirmation (mobile drawer/blockedUsers parity).
 */
import { useState } from 'react';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useUserConnections from '@wlb/shared/hooks/useUserConnections';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { UserConnectionItem } from '@wlb/shared/types/userProfile';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import {
  connectionAvatarUrl,
  connectionDisplayName,
  connectionProfileGuid,
} from '../features/profile/connections/connectionUser';
import '../features/more/more.css';

export function BlockedUsers() {
  const { t } = useTranslation();
  const { profile, loading: profileLoading } = useProfile();
  const [target, setTarget] = useState<UserConnectionItem | null>(null);

  const connections = useUserConnections({
    profileUserId: profile?.userId,
    tab: 'blocked',
    isOwnProfile: true,
  });

  const handleConfirm = async () => {
    if (!target) return;
    const result = await connections.runAction(target, 'unblock');
    setTarget(null);
    if (!result.success) {
      ToastService.show({
        type: 'error',
        message: result.message || t('blockedUsers.unblockError'),
      });
    }
  };

  const renderBody = () => {
    if ((connections.loading || profileLoading) && connections.users.length === 0) {
      return <MoreLoading />;
    }

    if (connections.error && connections.users.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={connections.error || t('blockedUsers.loadError')}
          onAction={connections.refresh}
          busy={connections.loading}
        />
      );
    }

    if (connections.users.length === 0) {
      return (
        <ListStatus
          variant="empty"
          title={t('blockedUsers.emptyTitle')}
          message={t('blockedUsers.emptyDescription')}
          onAction={connections.refresh}
          busy={connections.loading}
        />
      );
    }

    return (
      <>
        <ul className="more-list" aria-label={t('blockedUsers.accessibility.list')}>
          {connections.users.map(user => {
            const name = connectionDisplayName(user);
            return (
              <li className="blocked-row" key={user.userId}>
                <Avatar
                  src={connectionAvatarUrl(user)}
                  name={name}
                  username={user.userName}
                  seed={connectionProfileGuid(user) ?? user.userId}
                  size="lg"
                />
                <span className="blocked-row__name">{name}</span>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label={t('blockedUsers.accessibility.unblock', { name })}
                  onClick={() => setTarget(user)}
                >
                  {t('blockedUsers.unblock')}
                </Button>
              </li>
            );
          })}
        </ul>
        {connections.hasMore && (
          <div className="more-more">
            <Button
              size="sm"
              variant="outline"
              onClick={connections.loadMore}
              disabled={connections.loadingMore}
            >
              {t(connections.loadingMore ? 'connections.loadingMore' : 'connections.loadMore')}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <MorePage title={t('blockedUsers.title')}>
      {renderBody()}
      {target && (
        <ConfirmModal
          title={t('blockedUsers.unblockConfirm.title')}
          message={t('blockedUsers.unblockConfirm.message', {
            name: connectionDisplayName(target),
          })}
          confirmLabel={t('blockedUsers.unblockConfirm.confirm')}
          cancelLabel={t('common.buttons.cancel')}
          mediaUrl={connectionAvatarUrl(target) || null}
          loading={!!connections.actionLoading}
          onClose={() => setTarget(null)}
          onConfirm={() => { handleConfirm().catch(() => undefined); }}
        />
      )}
    </MorePage>
  );
}
